import { decodeFrame } from "@/lib/live/live-frame-codec";
import type { TimedLiveEvent } from "@/lib/live/live-events";
import { parseSessionState, type LiveSessionState } from "@/lib/live/session-state";
import {
  DEFAULT_SESSION_SIGNATURE,
  sessionTempoMap,
  SESSION_PPQ,
  type SessionSignature,
  type TempoChange,
} from "@/lib/live/session-tempo";
import type { AnalysisNote, AnalysisPedalEvent, AnalysisResult } from "@/lib/music/analyzer-types";
import { MusicalTimeline } from "@/lib/music/musical-timeline";
import { Note } from "@/lib/music/note";

import { findTakes } from "./replay-takes";
import type { ReplayManifestTake, ReplayPacket, ReplayStateLine } from "./replay-journal";

/**
 * Un récital enregistré, relu comme une partition.
 *
 * # Pourquoi un morceau, et pas un flux rejoué
 *
 * La première version rejouait les paquets à l'heure, comme une salle : les
 * notes apparaissaient au fil de la tête de lecture, la minimap restait vide, et
 * revenir en arrière effaçait ce qui avait été dessiné. Un enregistrement n'est
 * pas un direct : **c'est un morceau, et son futur est écrit**. Relu ainsi, il
 * hérite de tout ce que le studio sait déjà faire d'un fichier chargé — le
 * rouleau plein, la minimap entière, la molette, le glissé, la lecture, la
 * pause, la boucle.
 *
 * # Le tick est la vérité, la première note est l'origine
 *
 * Chaque geste enregistré porte le tick auquel l'interprète l'a joué, sur la
 * grille de sa séance. C'est cette grille qu'on reconstruit ici : même division
 * (`SESSION_PPQ`), mêmes tempos, même chiffrage, et les notes gardent leurs
 * écarts exacts. Chaque prise a la sienne, lue dans l'état en vigueur à sa
 * dernière note (voir `gridOf`).
 *
 * Mais ces ticks comptent depuis l'origine du transport de l'interprète, pas
 * depuis le début du récital : quelqu'un qui joue une minute après être entré
 * dans le studio produit des gestes au tick d'une minute. Le morceau
 * commencerait alors par une minute de silence. On ramène donc **la première
 * note au tick zéro**.
 *
 * # Le temps du morceau n'est pas celui de la salle
 *
 * Les images, elles, sont filmées à l'horloge de la salle, qui n'avance pas au
 * même rythme : mettre le récital en pause fige les ticks et laisse l'horloge
 * courir. Un récital du 2026-09-17 s'est ainsi arrêté dix-sept secondes en son
 * milieu — avec une origine unique, la vidéo restait figée une demi-minute,
 * repartait décalée d'autant, et continuait après la dernière note.
 *
 * On garde donc des **marques d'horloge** : à ce tick-là, il était cette
 * heure-là dans la salle. Elles se lisent dans les gestes eux-mêmes, qui
 * portent les deux — leur tick, et l'instant où l'enregistreur les a reçus. Une
 * marque naît quand les deux divergent, c'est-à-dire à chaque reprise après une
 * pause ou un déplacement de tête de lecture. C'est `wallClockAt` qui s'en sert
 * pour caler les images (`replay-camera.tsx`), et les lire dans les paquets
 * plutôt que dans les états de séance garde tout sur **une seule horloge**,
 * celle de l'enregistreur : la même que celle des images.
 *
 * # Ce qui n'a pas de fin
 *
 * Une note dont le relâchement manque — la liaison est tombée, le récital s'est
 * arrêté pendant qu'elle sonnait — reçoit une durée par défaut plutôt que de
 * s'étendre jusqu'au bout du morceau : une noire tenue une heure serait un mur
 * noir sur le rouleau.
 */

/**
 * La force d'un geste voyage en 0–1 dans une salle (`live-frame-codec.ts`), et
 * une lane l'attend dans la même échelle : rien à convertir entre les deux.
 */

/** À ce tick du morceau, il était cette heure-là dans la salle. */
export interface ReplayMark {
  tick: number;
  atMs: number;
}

/** Ce qu'on rend d'un enregistrement relu : un morceau, pour une prise. */
export interface ReplaySong {
  /** Le rang de la prise, tel qu'il a été compté pendant la séance. */
  number: number;
  /** Ce qu'on écrit à sa frontière sur le rouleau : « Prise 2 ». */
  label: string;
  analysis: AnalysisResult;
  /**
   * Les marques d'horloge du morceau, par tick croissant. La première est le
   * tick zéro ; les suivantes, les reprises. Vide quand rien n'a été joué.
   */
  marks: readonly ReplayMark[];
  /** Combien de notes ont été retrouvées. Zéro : il n'y a rien à montrer. */
  notes: number;
}

/** La durée donnée à une note dont le relâchement manque : une croche. */
const ORPHAN_TICKS = SESSION_PPQ / 2;

/** Ce qu'on laisse respirer après la dernière note, pour que la fin ne colle pas au bord. */
const TAIL_TICKS = SESSION_PPQ * 2;

/**
 * L'écart entre les deux temps au-delà duquel on pose une marque, en
 * millisecondes.
 *
 * En dessous, c'est la gigue du réseau : un paquet arrive en retard, et son
 * tick ne dit plus tout à fait l'heure. Au-dessus, c'est une pause ou un
 * déplacement de tête de lecture — un fait de séance, pas du bruit. Une seconde
 * sépare les deux largement : une gigue d'une seconde aurait rendu le récital
 * injouable, et une pause d'une seconde décalerait l'image d'aussi peu.
 */
const MARK_DRIFT_MS = 1000;

/**
 * Relit les paquets d'un enregistrement et en fait **un morceau par prise**.
 *
 * Les états de séance donnent la grille — tempos, chiffrage, division — et le
 * point de rattachement entre les ticks et l'horloge de la salle.
 *
 * Les prises viennent du manifeste quand l'enregistreur les y a écrites : ce
 * sont alors des **faits**, le compteur que l'interprète a incrémenté en
 * jouant. Sans elles — un enregistrement d'avant le signal —, on retombe sur
 * la reconnaissance du début rejoué (`replay-takes.ts`), qui reste une
 * proposition. Dans les deux cas, chaque prise repart au tick zéro : c'est ce
 * qui permet de comparer deux essais au même endroit de l'écran.
 */
export function buildReplaySongs(
  packets: readonly ReplayPacket[],
  states: readonly ReplayStateLine[],
  title: string,
  takes: readonly ReplayManifestTake[] = [],
): ReplaySong[] {
  const readings = readingsOf(states);
  const gestures = gesturesOf(packets);
  const cuts = cutsOf(gestures, takes, readings, title);

  return cuts.map(({ number, from, to }) =>
    songOf(gestures.slice(from, to), readings, title, number, cuts.length > 1),
  );
}

/** Un état de séance relu, et l'heure où l'enregistreur l'a reçu. */
interface Reading {
  receivedAtMs: number;
  state: LiveSessionState;
}

/** Les états lisibles, dans l'ordre de leur arrivée. */
function readingsOf(states: readonly ReplayStateLine[]): Reading[] {
  const readings: Reading[] = [];

  for (const line of states) {
    const state = parseSessionState(line.metadata);

    if (state !== null) {
      readings.push({ receivedAtMs: line.receivedAtMs, state });
    }
  }

  return readings;
}

/**
 * L'état en vigueur à cette heure-là : le dernier reçu avant elle, sinon le
 * premier de tous — un geste arrivé avant le premier état a été joué sur la
 * grille que cet état décrit.
 */
function stateAt(readings: readonly Reading[], atMs: number): LiveSessionState | null {
  let found = readings[0]?.state ?? null;

  for (const reading of readings) {
    if (reading.receivedAtMs > atMs) {
      break;
    }

    found = reading.state;
  }

  return found;
}

/** La grille d'un morceau : son tempo de départ, ses changements, son chiffrage. */
interface SongGrid {
  timeline: MusicalTimeline;
  bpm: number;
  changes: TempoChange[];
  signature: SessionSignature;
}

/**
 * La grille d'une tranche de gestes, ses ticks comptés depuis le premier.
 *
 * C'est celle que le studio installera en chargeant le morceau, donc celle qui
 * dit combien de temps séparera deux ticks à la lecture — et celle contre
 * laquelle se posent les marques d'horloge.
 *
 * Lue dans l'état en vigueur à la **dernière** note : un réglage fait entre
 * deux prises, ou juste avant la première note, quand la métadonnée est encore
 * en route, vaut pour la prise qui suit ; un réglage fait après elle ne la
 * touche pas. Jusqu'au 2026-09-22, le tempo du premier état valait pour tout
 * l'enregistrement, et le chiffrage était 4/4 quoi qu'on ait joué.
 */
function gridOf(gestures: readonly Gesture[], readings: readonly Reading[]): SongGrid {
  const last = gestures[gestures.length - 1];
  const state = last === undefined ? (readings[0]?.state ?? null) : stateAt(readings, last.atMs);
  const map = sessionTempoMap(state?.tempoBpm ?? 0, state?.tempoMarks ?? [], SESSION_PPQ, gestures[0]?.tick ?? 0);
  const signature = state?.signature ?? DEFAULT_SESSION_SIGNATURE;
  let bpm = map.bpm;
  const changes: TempoChange[] = [];

  // Au tick entier, comme le MIDI que l'enregistreur écrit de la même prise :
  // relue par l'analyseur ou depuis les journaux, elle tombe au même endroit.
  for (const change of map.changes) {
    const tick = Math.round(change.tick);

    if (tick <= 0) {
      bpm = change.bpm;

      continue;
    }

    changes.push({ tick, bpm: change.bpm });
  }

  const timeline = new MusicalTimeline(SESSION_PPQ, bpm, {
    numerator: signature.beats,
    denominator: signature.unit,
  });

  for (const change of changes) {
    timeline.addTempo(change.tick, change.bpm);
  }

  return { timeline, bpm, changes, signature };
}

/** Un geste enregistré, tel qu'il servira : son tick, son heure, sa nature. */
interface Gesture {
  tick: number;
  atMs: number;
  event: TimedLiveEvent["event"];
}

/** Déplie les paquets en gestes, dans l'ordre de leur arrivée. */
function gesturesOf(packets: readonly ReplayPacket[]): Gesture[] {
  const gestures: Gesture[] = [];

  for (const packet of packets) {
    const frame = decodeFrame(packet.payload);

    // Seuls les gestes font un morceau : une trame d'état redit ce qui est
    // tenu à cet instant, et la rejouer doublerait des notes déjà écrites.
    if (frame === null || frame.type !== "gestures") {
      continue;
    }

    for (const timed of frame.events) {
      gestures.push({ tick: timed.tick, atMs: packet.receivedAtMs, event: timed.event });
    }
  }

  return gestures;
}

/** Où couper, et sous quel rang. */
interface Cut {
  number: number;
  from: number;
  to: number;
}

/**
 * Les coupes, par ordre de préférence : ce que l'enregistreur a constaté,
 * sinon ce que la relecture reconnaît, sinon rien.
 *
 * Les bornes du manifeste sont en ticks de séance, comme les gestes : on
 * compare ce qui se compare. Une prise du manifeste sans tick — elle ne porte
 * aucun geste — ne coupe rien, il n'y aurait rien à montrer dedans.
 */
function cutsOf(
  gestures: readonly Gesture[],
  takes: readonly ReplayManifestTake[],
  readings: readonly Reading[],
  title: string,
): Cut[] {
  if (gestures.length === 0) {
    return [{ number: 1, from: 0, to: 0 }];
  }

  const written = takes
    .filter((take) => typeof take.startTick === "number")
    .sort((a, b) => (a.startTick ?? 0) - (b.startTick ?? 0));

  // Une seule prise écrite compte autant que dix : c'est un fait, pas une
  // majorité. La tenir pour rien renvoyait à l'heuristique, qui découpait un
  // enregistrement que l'interprète n'avait pas découpé — et les rangs
  // inventés ne correspondaient plus à ceux de l'API, donc plus d'analyse.
  if (written.length > 0) {
    return boundedBy(gestures, written.map((take) => ({ number: take.number, tick: take.startTick ?? 0 })));
  }

  // Sans prises écrites : le début rejoué après un blanc. On bâtit un morceau
  // provisoire pour le donner à lire — c'est sur des notes que la
  // reconnaissance travaille, pas sur des gestes.
  const provisional = songOf(gestures, readings, title, 1, false);
  const found = findTakes(
    provisional.analysis.notes,
    gridOf(gestures, readings).timeline,
    provisional.analysis.metadata.totalTicks,
  );

  if (found.length < 2) {
    return [{ number: 1, from: 0, to: gestures.length }];
  }

  const origin = gestures[0].tick;

  return boundedBy(
    gestures,
    found.map((run, index) => ({ number: index + 1, tick: run.startTick + origin })),
  );
}

/** Découpe les gestes aux ticks donnés, dans l'ordre. */
function boundedBy(gestures: readonly Gesture[], starts: readonly { number: number; tick: number }[]): Cut[] {
  const cuts: Cut[] = [];

  for (let index = 0; index < starts.length; index += 1) {
    const from = gestures.findIndex((gesture) => gesture.tick >= starts[index].tick);
    const next = starts[index + 1];
    const to = next === undefined
      ? gestures.length
      : gestures.findIndex((gesture) => gesture.tick >= next.tick);

    if (from < 0 || (to >= 0 && to <= from)) {
      continue;
    }

    cuts.push({ number: starts[index].number, from, to: to < 0 ? gestures.length : to });
  }

  return cuts.length > 0 ? cuts : [{ number: 1, from: 0, to: gestures.length }];
}

/** Bâtit un morceau à partir d'une tranche de gestes. */
function songOf(
  gestures: readonly Gesture[],
  readings: readonly Reading[],
  title: string,
  number: number,
  several: boolean,
): ReplaySong {
  const grid = gridOf(gestures, readings);
  const notes: AnalysisNote[] = [];
  const pedals: AnalysisPedalEvent[] = [];
  /** Les notes commencées et pas encore relâchées, par hauteur. */
  const held = new Map<number, AnalysisNote>();
  /** Le premier geste : son tick devient le zéro du morceau. */
  let anchor: ReplayMark | null = null;
  /** Les marques d'horloge, au tick du morceau. La dernière est en cours. */
  const marks: ReplayMark[] = [];

  for (const gesture of gestures) {
    const { event } = gesture;

    anchor ??= { tick: gesture.tick, atMs: gesture.atMs };

    const tick = gesture.tick - anchor.tick;

    mark(marks, grid.timeline, tick, gesture.atMs);

    if (event.kind === "sustain") {
      pedals.push({ tick, kind: "sustain", down: event.down });

      continue;
    }

    if (event.kind === "noteOn") {
      // Une hauteur rejouée sans relâchement ferme la précédente : c'est ce
      // que fait un clavier, et deux notes superposées au même endroit ne se
      // distingueraient pas à l'écran.
      close(held, event.midi, tick);

      const note = analysisNote(event.midi, tick, event.velocity);

      notes.push(note);
      held.set(event.midi, note);

      continue;
    }

    close(held, event.midi, tick);
  }

  // Ce qui sonnait encore à la fin : une durée courte, pas jusqu'au bout.
  for (const [, note] of held) {
    note.durationTicks = ORPHAN_TICKS;
  }

  notes.sort((a, b) => a.tick - b.tick);

  const lastTick = notes.reduce((end, note) => Math.max(end, note.tick + note.durationTicks), 0);
  const totalTicks = lastTick + TAIL_TICKS;

  return {
    number,
    // Une seule traversée n'a pas de rang à porter : c'est le morceau, pas
    // « la prise 1 ».
    label: several ? `Prise ${number}` : title,
    analysis: analysisOf(notes, pedals, grid, totalTicks, title),
    marks,
    notes: notes.length,
  };
}

/**
 * Pose une marque d'horloge si ce geste ne tombe pas là où la précédente le
 * promettait : la séance a été mise en pause, ou sa tête de lecture déplacée.
 */
function mark(
  marks: ReplayMark[],
  grid: MusicalTimeline,
  tick: number,
  atMs: number,
): void {
  const last = marks[marks.length - 1];

  if (last !== undefined) {
    const promised = last.atMs + (grid.tickToSeconds(tick) - grid.tickToSeconds(last.tick)) * 1000;

    if (Math.abs(atMs - promised) <= MARK_DRIFT_MS) {
      return;
    }
  }

  marks.push({ tick, atMs });
}

/**
 * L'heure qu'il était dans la salle à ce tick du morceau.
 *
 * C'est le pont entre le morceau et les images : une image porte l'heure de sa
 * prise, la tête de lecture porte un tick. Le temps d'une pause n'a pas de tick
 * — les images de ce moment-là ne se montrent donc jamais, ce qui est juste :
 * il ne s'y jouait rien.
 */
export function wallClockAt(
  tick: number,
  marks: readonly ReplayMark[],
  tickToSeconds: (tick: number) => number,
): number {
  let found: ReplayMark | undefined = marks[0];

  for (const candidate of marks) {
    if (candidate.tick > tick) {
      break;
    }

    found = candidate;
  }

  if (found === undefined) {
    return Number.NaN;
  }

  return found.atMs + (tickToSeconds(tick) - tickToSeconds(found.tick)) * 1000;
}

function close(held: Map<number, AnalysisNote>, midi: number, tick: number): void {
  const note = held.get(midi);

  if (note === undefined) {
    return;
  }

  // Un relâchement au même tick que la frappe — deux gestes dans la même
  // milliseconde — donnerait une note invisible.
  note.durationTicks = Math.max(1, tick - note.tick);
  held.delete(midi);
}

function analysisNote(midi: number, tick: number, velocity: number): AnalysisNote {
  return {
    tick,
    durationTicks: ORPHAN_TICKS,
    midi,
    name: Note.fromMidi(midi).toName(),
    velocity,
    track: 0,
    harmony: null,
    sectionId: null,
    hand: null,
  };
}

/**
 * Le tout, dans la forme que le studio sait charger.
 *
 * C'est la même forme qu'un exercice engendré ou que le jingle d'accueil : un
 * `AnalysisResult` en mémoire, que `createSongFromRawAnalysis` accepte sans
 * chemin particulier.
 */
function analysisOf(
  notes: AnalysisNote[],
  pedals: AnalysisPedalEvent[],
  grid: SongGrid,
  totalTicks: number,
  title: string,
): AnalysisResult {
  const { beats, unit } = grid.signature;
  // Une mesure : `beats` temps d'un `unit`-ième de ronde, la ronde valant
  // quatre noires.
  const measureTicks = (SESSION_PPQ * 4 * beats) / unit;

  return {
    metadata: {
      ppq: SESSION_PPQ,
      totalTicks,
      measures: Math.max(1, Math.ceil(totalTicks / measureTicks)),
      totalNotes: notes.length,
      trackCount: 1,
      format: 1,
      filename: title,
    },
    tempo: {
      ppq: SESSION_PPQ,
      defaultBpm: grid.bpm,
      beatsPerMeasure: beats,
      tempoChanges: [{ tick: 0, bpm: grid.bpm }, ...grid.changes],
      timeSignatures: [{ tick: 0, numerator: beats, denominator: unit }],
      pedals,
    },
    // Rien de tout cela n'a été analysé : un récital enregistré n'est pas passé
    // par l'analyseur. On remplit ce que la forme exige, sans rien affirmer —
    // pas de tonalité devinée, pas d'accords, pas de phrases.
    key: {
      tonic: "C",
      mode: "major",
      label: "",
      confidence: 0,
      relative: { tonic: "A", mode: "minor", label: "" },
      topCandidates: [],
    },
    tracks: [
      {
        index: 0,
        name: title,
        channel: 0,
        instrument: "piano",
        noteCount: notes.length,
        range: range(notes),
        avgVelocity: average(notes),
        role: null,
      },
    ],
    notes,
    chords: null,
    sections: [],
    phrases: { melodyTrack: 0, count: 0, items: [] },
    statistics: {
      form: "",
      uniqueChords: 0,
      chordFrequency: [],
      pitchDistribution: [],
      sectionCount: 0,
      totalMeasures: 0,
    },
  };
}

/** L'étendue jouée, telle que la forme d'une piste l'attend. */
function range(notes: readonly AnalysisNote[]) {
  const midis = notes.map((note) => note.midi);
  const lowest = midis.length === 0 ? 60 : Math.min(...midis);
  const highest = midis.length === 0 ? 60 : Math.max(...midis);

  return {
    lowest,
    highest,
    lowestName: Note.fromMidi(lowest).toName(),
    highestName: Note.fromMidi(highest).toName(),
  };
}

/** La force moyenne, en MIDI : c'est l'échelle des pistes de l'analyseur. */
function average(notes: readonly AnalysisNote[]): number {
  if (notes.length === 0) {
    return 0;
  }

  const sum = notes.reduce((total, note) => total + note.velocity, 0);

  return Math.round((sum / notes.length) * 127);
}
